import React, { useState, useEffect, useContext } from 'react';
import { ShieldBan, Trash2, Plus } from 'lucide-react';
import { SnackbarContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import api from '../api/api';
import { useConfirm } from '../contexts/ConfirmContext';

export default function Suppressions() {
    const showSnackbar = useContext(SnackbarContext);
    const [suppressions, setSuppressions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create new suppression state
    const [newEmail, setNewEmail] = useState('');
    const [newReason, setNewReason] = useState('BOUNCED');

    const fetchSuppressions = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/suppressions');
            // Assumes backend returns { suppressions: [...] } or just an array
            setSuppressions(res.data.suppressions || res.data);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSuppressions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApiError = (error) => {
        if (error.isAppError) {
            showSnackbar(`${error.code}: ${error.message}`, 'error');
        } else {
            showSnackbar('An unexpected error occurred.', 'error');
        }
    };

    const handleAddSuppression = async (e) => {
        e.preventDefault();
        try {
            await api.post('/api/suppressions', {
                email: newEmail,
                reason: newReason
            });
            showSnackbar('Suppression added successfully.', 'success');
            setNewEmail('');
            setNewReason('BOUNCED');
            fetchSuppressions();
        } catch (error) {
            handleApiError(error);
        }
    };

    const confirm = useConfirm();

    const handleRelease = async (id, email) => {
        const ok = await confirm("Release Suppression?", `Are you sure you want to release ${email} from the blocklist? This may impact sender reputation.`);
        if (!ok) return;

        try {
            const res = await api.delete(`/api/suppressions/${id}`);

            // Reconnect Sprint 6 Core Feature: Pending Approval Handling
            if (res.status === 202) {
                showSnackbar(`Release requires Maker/Checker approval. Placed in pending queue.`, 'default');
            } else {
                showSnackbar('Suppression released directly.', 'success');
            }
            fetchSuppressions();
        } catch (error) {
            handleApiError(error);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Suppression Blocklist</h1>
                    <p className="text-muted-foreground mt-2">
                        Manage global bounds and abuse complaints.
                    </p>
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">

                {/* Add Suppression Form */}
                <div className="md:col-span-1 border rounded bg-card shadow-sm p-6 h-fit">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <Plus className="h-5 w-5 mr-2" /> Add to Blocklist
                    </h3>
                    <form onSubmit={handleAddSuppression} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Email Address</label>
                            <Input
                                type="email"
                                required
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                placeholder="spam@example.com"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Reason Category</label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background"
                                value={newReason}
                                onChange={(e) => setNewReason(e.target.value)}
                            >
                                <option value="BOUNCED">Hard Bounce</option>
                                <option value="COMPLAINT">Spam Complaint</option>
                                <option value="UNSUBSCRIBED">Unsubscribed</option>
                                <option value="MANUAL_BLOCK">Manual Block</option>
                            </select>
                        </div>
                        <Button type="submit" className="w-full">Block Email</Button>
                    </form>
                </div>

                {/* Suppression List */}
                <div className="md:col-span-2 border rounded bg-card shadow-sm">
                    <div className="p-6">
                        {loading ? (
                            <div className="py-4 text-center">Loading Blocklist...</div>
                        ) : suppressions.length === 0 ? (
                            <div className="py-12 text-center text-muted-foreground">
                                <ShieldBan className="mx-auto h-8 w-8 mb-3 opacity-50" />
                                No suppressed emails found in this workspace.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs uppercase bg-muted text-muted-foreground">
                                        <tr>
                                            <th className="px-4 py-3">Email</th>
                                            <th className="px-4 py-3">Reason</th>
                                            <th className="px-4 py-3">Status</th>
                                            <th className="px-4 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {suppressions.map((sup) => (
                                            <tr key={sup._id} className="border-b last:border-0 hover:bg-muted/50">
                                                <td className="px-4 py-3 font-medium">{sup.email}</td>
                                                <td className="px-4 py-3">
                                                    <Badge variant="outline">{sup.reason}</Badge>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {sup.isActive ? (
                                                        <span className="flex items-center text-red-600 bg-red-100 px-2 py-1 rounded w-max text-xs font-bold">
                                                            <ShieldBan className="w-3 h-3 mr-1" /> BLOCKED
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground text-xs italic">Released</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    {sup.isActive && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                                                            onClick={() => handleRelease(sup._id, sup.email)}
                                                            title="Request Release"
                                                        >
                                                            <Trash2 className="h-4 w-4 mr-2" /> Release
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
